import { IconButton } from "@mui/material";
import { useHandler } from "@redotech/react-util/hook";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { SettingsPermissions } from "@redotech/redo-model/permissions";
import { FlowType } from "@redotech/redo-model/return-flow";
import { TriggerType } from "@redotech/redo-model/return-flow/trigger";
import {
  TriggerState,
  triggerTypeLabel,
} from "@redotech/redo-return-ui/flows/return-flow/trigger";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import QuestionIcon from "@redotech/redo-web/icon-old/question.svg";
import { SelectDropdown } from "@redotech/redo-web/select-dropdown";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo } from "react";

const getTriggers = (flowType: FlowType): TriggerType[] => {
  if (flowType === FlowType.RULE) {
    return [
      TriggerType.CONVERSATION_MESSAGE_CREATED,
      TriggerType.MERCHANT_MESSAGE_CREATED,
      TriggerType.CONVERSATION_CREATED,
      TriggerType.CHAT_CONVERTED_TO_EMAIL,
    ];
  } else {
    return [];
  }
};

const getInfo = (type: TriggerType) => {
  switch (type) {
    case TriggerType.CONVERSATION_CREATED: {
      return "This only fires once for a ticket, when it is created.";
    }
    case TriggerType.CONVERSATION_MESSAGE_CREATED: {
      return "This fires when a ticket is created, and every time the customer sends a new message";
    }
    case TriggerType.MERCHANT_MESSAGE_CREATED: {
      return "This fires every time a support agent sends a new message";
    }
    // No default
  }
  return null;
};

export function triggerTypeLabelWithTooltip(type: TriggerType) {
  const labelText = triggerTypeLabel(type);
  const info = getInfo(type);
  return info ? (
    <div>
      <Tooltip title={info}>
        <IconButton>
          <QuestionIcon />
        </IconButton>
      </Tooltip>
      {labelText}
    </div>
  ) : (
    labelText
  );
}

export const TriggerDetails = memo(function TriggerDetails({
  stepSelect,
  state,
  setState,
  flowType,
}: StepTypeDetailsProps<TriggerState>) {
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );

  const nextStepChange = useHandler((next: StepId | undefined) => {
    setState((state) => ({ ...state, next }));
  });
  const handleType = useHandler(async (triggerType: TriggerType) => {
    setState((state) => ({ ...state, triggerType }));
  });

  const triggers = getTriggers(flowType);
  return (
    <>
      <Text fontSize="xs" textColor="secondary">
        Type
      </Text>
      <SelectDropdown
        disabled={!canEditReturnsSettings}
        options={triggers}
        value={state.triggerType}
        valueChange={handleType}
      >
        {(option) => triggerTypeLabelWithTooltip(option)}
      </SelectDropdown>
      <Text fontSize="xs" textColor="secondary">
        Next step
      </Text>
      {!state.next && (
        <Text fontSize="xs" fontWeight="regular" textColor="error">
          Required
        </Text>
      )}
      {stepSelect({ value: state.next, valueChange: nextStepChange })}
    </>
  );
});
